/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import DeleteIcon from "@mui/icons-material/Delete";
import RefreshOutlinedIcon from "@mui/icons-material/Refresh";
import {CircularProgress, IconButton, Tooltip} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {toast} from "react-hot-toast";
import {usePostRequest} from "../../../api/helper";
import {tableButtonGray} from "../../../constants/colors";
import VAR_COMMON_CONSTANTS, {SENSOR_TYPES} from "../../../constants/globalVariables";
import {stringToDateToLocaleString} from "../../../helpers/helpers";
import {useHasWriteAccess} from "../../../redux/ducks/AccessControl/accessControlHelpers";
import {ELoadingStateTypes, selectLoadingState, setLoadingState1} from "../../../redux/ducks/Misc/slice";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks";
import ArgosButton, {ArgosLoadingButton} from "../../components/Buttons/ArgosButton";
import ArgosFlexbox from "../../components/Containers/ArgosFlexbox";
import ArgosDialog from "../../components/Dialogs/ArgosDialog";
import AddNewSensorPopUp from "../../components/Dialogs/SensorsDialogs/AddNewSensorPopUp";
import {ArgosAddIcon} from "../../components/Icons/ArgosActionIcons";
import {DefaultButtonSpinner} from "../../components/Loader/DefaultButtonSpinner";
import {ArgosTable} from "../../components/Table/ArgosTable";
import {SENSOR_REQUEST_ENDPOINTS} from "../radar/SensorRadar/constants";
import {addLidarEndpoint, getLidarLandingEndpoint} from "./constants";
import {setGlobalDialogComponent, setIsGlobalDialogOpen} from "../../../redux/ducks/GlobalDialog/slice";
import {setGlobalRightClickMenuData, setGlobalRightClickMenuPosition} from "../../../redux/ducks/GlobalRightClickMenu/slice";
import PageviewOutlined from "@mui/icons-material/PageviewOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {config} from "../../../AppConfig/config";
import {loginRequest, requestIdToken} from "../../../auth/authConfig";
import {useMsal} from "@azure/msal-react";

const SENSOR_DISPLAY_NAME = VAR_COMMON_CONSTANTS.LIDAR
const SENSOR_TYPE_NAME = SENSOR_TYPES.LIDAR

const Table = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const columns = useMemo(
    () => [
      {
        accessorFn: (row: any) => row?.sub ? row.supplier ?? '' : "",
        id: 'supplier',
        header: 'Supplier',
        size: 300,
      },
      {
        accessorFn: (row: any) => row?.sub ? row.component ?? '' : "",
        id: 'component',
        header: 'Component',
        size: 300,
      },
      {
        accessorFn: (row: any) => row?.sub ? "" : row?.version ?? '',
        id: 'version',
        header: 'Latest Version',
        size: 300,
      },
      {
        accessorFn: (row: any) => row?.sub ? "" : stringToDateToLocaleString(row?.dateCreated) ?? '',
        id: 'dateCreated',
        header: 'Last Updated',
        size: 300,
      },
    ],
    []
  )

  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isTableError, setIsTableError] = useState(false)
  const setTableDataHelper = (data: any, success: boolean) => {
    if (success) {
      setIsTableError(false)
      setTableData(data?.sensors_to_plot ?? [])
      setIsTableLoading(false)
    } else {
      setIsTableError(true)
      setIsTableLoading(false)
      setTableData([])
    }
  }

  const fetchHandle = usePostRequest(getLidarLandingEndpoint)
  const fetchData = useCallback(async () => {
    setIsTableLoading(true);
    fetchHandle().then((data) => {
      setTableDataHelper(data, true)
    }).catch(err => {
      console.log(err)
      setTableDataHelper([], false)
    }).finally(() => {
      // setIsTableLoading(false)
    })
  }, [])

  useEffect(() => {
    setIsTableLoading(true)
    fetchData()
  }, [])


  const hasWriteAccess = useHasWriteAccess()
  const [isDownloadingFile, setIsDownloadingFile] = useState<boolean>(false)

  const [isOpen, setIsOpen] = useState(false);
  const handleOpenAddRadarDialog = () => {
    setIsOpen(true);
  }

  const actions = (
    <div style={{justifyContent: "right"}}>
      <Tooltip title="Refresh" arrow style={{paddingTop: "7px"}}>
        <IconButton onClick={fetchData} size="large">
          <RefreshOutlinedIcon style={{color: tableButtonGray}}/>
        </IconButton>
      </Tooltip>
      {
        hasWriteAccess && <Tooltip title={`Add New ${SENSOR_DISPLAY_NAME}`} arrow style={{paddingTop: "7px"}}>
          <IconButton onClick={handleOpenAddRadarDialog} size='large'>
            <ArgosAddIcon style={{color: tableButtonGray}}/>
          </IconButton>
        </Tooltip>
      }
    </div>
  );

  const togglePopUp = () => {
    setIsOpen(!isOpen);
  }

  const onClickDeleteSensor = (row: any) => {
    /**
     * When deleting a sensor, first check if it is allowed.
     */
    checkIsDeleteValid(row)
  }
  const checkIsDeleteValidHandle = usePostRequest(SENSOR_REQUEST_ENDPOINTS.shouldDeleteSensor)
  const [deleteDialogData, setDeleteDialogData] = useState<any>(null)

  const dispatch = useAppDispatch()

  const checkIsDeleteValid = (data: any) => {
    dispatch(setLoadingState1(ELoadingStateTypes.DELETE_RADAR, true, {id: data.id}))
    let _deleteDialogData: any = {
      data: data
    }
    checkIsDeleteValidHandle({id: data.id}).then((res) => {
      if (res?.shouldDeleteSensor) {
        // Show message of what will be deleted and are you sure.
        _deleteDialogData.message = res?.message
      } else {
        // Show that the sensor cannot be deleted and why.
        _deleteDialogData.deleteInvalid = true
        _deleteDialogData.message = res?.message
      }
      setDeleteDialogData(_deleteDialogData)
    }).catch(err => {
      console.log(err)
      toast.error("Failed to delete sensor")
      _deleteDialogData.deleteInvalid = true
      _deleteDialogData.message = "Failed to delete sensor"
      setDeleteDialogData(_deleteDialogData)
    }).finally(() => {
      dispatch(setLoadingState1(ELoadingStateTypes.DELETE_RADAR, false, {id: data.id}))
    })
  }
  const deleteSensorHandle = usePostRequest(SENSOR_REQUEST_ENDPOINTS.deleteSensor)
  const deleteSensor = (data: any) => {
    dispatch(setLoadingState1(ELoadingStateTypes.DELETE_RADAR, true, {id: data.id}))
    deleteSensorHandle({id: data.id, sensorType: SENSOR_TYPE_NAME}).then((res) => {
      setDeleteDialogData(null)
      setTableDataHelper(res, true)
    }).catch(err => {
      console.log(err)
      toast.error("Failed to delete sensor")
      const payload = {
        data,
        deleteInvalid: true,
        message: "Failed to delete sensor"
      }
      setDeleteDialogData(payload)
    }).finally(() => {
      dispatch(setLoadingState1(ELoadingStateTypes.DELETE_RADAR, false, {id: data.id}))
    })
  }
  const {instance, accounts} = useMsal();

  const handleDownloadTemplateFile = async (row: any) => {
    let url = `${config.url.API_URL}/HttpTriggerDownloadFile?request=sensorLidarSheet`
    const idTok = await requestIdToken(instance, accounts)
    const supplier = row.original.supplier.replace("/", "_")
    const component = row.original.component.replace("/", "_")
    const version = row.original.version
    const id = row.original.id
    const fileName = `${supplier}_${component}_${version}_${id}.xlsx`
    setIsDownloadingFile(true)
    const req_body = {
      "id": id,
      "fileName": fileName,
      "supplier": supplier,
      "component": component,
      "version": version
    }
    fetch(url, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
        'Authorization': `Bearer ${idTok}`
      },
      body: JSON.stringify(req_body)
    }).then((res) => {
      if (res.status === 401)
        instance.loginRedirect(loginRequest).catch(e => {
            console.log(e)
          }
        )
      res.blob().then((blob) => {
        const fileURL = window.URL.createObjectURL(blob)
        let alink = document.createElement('a')
        alink.href = fileURL;
        alink.download = fileName
        alink.click();
        setIsDownloadingFile(false)
      })
    }).catch((e) => {
      console.log(e)
    })

  }

  const isDeleteRadarLoadingState = useAppSelector((state) => selectLoadingState(state, ELoadingStateTypes.DELETE_RADAR))
  const isDeleteRadarLoading = isDeleteRadarLoadingState?.isLoading ?? false
  const isDeleteRadarLoadingData = isDeleteRadarLoadingState?.data ?? null

  const rowActions = (row: any) => {
    const _row = row.original
    const id = _row?.id
    const url = `/lidarPerformance?id=${id}`

    const isCurrentRadarDeleteLoading = isDeleteRadarLoadingData?.id === id
    // const deleteDisabled = !isCurrentRadarDeleteLoading && isDeleteRadarLoading

    const isViewSensorDetailsDisabled = false
    const isDownloadSensorTemplateDisabled = false
    return (
      _row.sub ? "" :
        <ArgosFlexbox>
          <Tooltip title={"View LiDAR Performance"} arrow>
            <a
              onClick={(e) => {
                if(isViewSensorDetailsDisabled) return
                e.preventDefault()
                const component = {
                  'params': {id},
                  'name': 'SensorLidarItemDetails'
                }
                dispatch(setGlobalDialogComponent(component))
                dispatch(setIsGlobalDialogOpen(true))
              }}
              onContextMenu={(e) => {
                if(isViewSensorDetailsDisabled) return
                e.preventDefault()
                dispatch(setGlobalRightClickMenuData({url}))
                dispatch(setGlobalRightClickMenuPosition({x: e.pageX, y: e.pageY}))
              }}
              href={isViewSensorDetailsDisabled ? undefined : url}
              target={isViewSensorDetailsDisabled ? undefined : "_blank"}
            >
              <IconButton size={'large'} disabled={isViewSensorDetailsDisabled}>
                <PageviewOutlined fontSize="medium" style={{color: tableButtonGray}}/>
              </IconButton>
            </a>
          </Tooltip>

          {isDownloadingFile ? <CircularProgress/> : hasWriteAccess &&
            <Tooltip title="Download Sensor LiDAR Template" arrow>
              <IconButton
                onClick={() => {
                  if(isDownloadSensorTemplateDisabled) return
                  handleDownloadTemplateFile(row)
                }}
                size={'large'}
                disabled={isDownloadSensorTemplateDisabled}
              >
                <FileDownloadIcon style={{color: tableButtonGray}}/>
              </IconButton>
            </Tooltip>}
          {
            hasWriteAccess && (<Tooltip title={`Delete ${SENSOR_DISPLAY_NAME}`} arrow>
              <IconButton
                disabled={isDeleteRadarLoading}
                onClick={(e) => {
                  // deleteProgram({id: adasProgramId, name: programName, modelYear})
                  const rowData = row.original
                  onClickDeleteSensor(rowData)
                }}
                size="large"
              >
                {
                  isDeleteRadarLoading ?
                    <DefaultButtonSpinner/> :
                    <DeleteIcon fontSize="medium" style={{color: tableButtonGray}}/>
                }
              </IconButton>
            </Tooltip>)
          }
        </ArgosFlexbox>
    )
  }


  const deleteDialogMessage = deleteDialogData?.message
  const sensorToDeleteSupplier = deleteDialogData?.data?.supplier
  const sensorToDeleteComponent = deleteDialogData?.data?.component
  const sensorToDeleteVersion = deleteDialogData?.data?.version
  const sensorToDeleteName = `${sensorToDeleteSupplier} ${sensorToDeleteComponent} ${sensorToDeleteVersion}`

  return (
    <div style={{overflow: 'auto', flex: 1}}>
      <ArgosTable
        uid={'lidar_1'}
        columns={columns}
        data={tableData}

        // initialState={{ pagination: { pageSize: 20, pageIndex: 1}}}
        enableExpanding
        state={{isLoading: isTableLoading}}
        muiExpandButtonProps={({row}) => ({
          // Show expand button only when row has sub-elements
          sx: {
            display: row.subRows?.length ? 'flex' : 'none'
          }
        })}
        enableRowActions
        renderTopToolbarCustomActions={() => actions}
        positionActionsColumn="last"
        displayColumnDefOptions={{
          'mrt-row-actions': {
            size: 200,
          },
        }}
        getSubRows={(originalRow) => originalRow?.sub}
        renderRowActions={({row}: any) => rowActions(row)}
        muiTopToolbarProps={{
          sx: {justifyContent: "right !important"}
        }}
      />

      <ArgosDialog open={isOpen} maxWidth={'fit-content'} minWidth={'0px'} height={'fit-content'}
                   headerTitle={`Add New ${SENSOR_DISPLAY_NAME}`} headerTitleAlign={'left'} onClose={() => null}>
        <AddNewSensorPopUp handleClose={togglePopUp} setTableData={() => fetchData()} sensorType={SENSOR_TYPE_NAME}
                           currentTableData={tableData} addSensorEndpoint={addLidarEndpoint}/>
      </ArgosDialog>

      <ArgosDialog
        minWidth={'0px'} maxWidth={'30vw'} height={'fit-content'}
        open={deleteDialogData}
        onClose={() => {
          setDeleteDialogData(false)
        }}
        headerTitle={`Delete ${SENSOR_DISPLAY_NAME} ${sensorToDeleteName}`}
        headerTitleAlign={'left'}
      >
        <>
          {!deleteDialogData ? <></> :
            <ArgosFlexbox vertical>
              <div>{deleteDialogMessage}</div>
              <ArgosFlexbox alignItems={'center'} justifyContent={'center'}>
                <ArgosButton
                  disabled={isDeleteRadarLoading}
                  onClick={() => {
                    setDeleteDialogData(null)
                  }}
                >
                  CANCEL
                </ArgosButton>
                {
                  !deleteDialogData?.deleteInvalid &&
                  <ArgosLoadingButton
                    disabled={isDeleteRadarLoading}
                    onClick={() => {
                      deleteSensor(deleteDialogData?.data)
                    }}
                    isLoading={isDeleteRadarLoading}
                  >
                    DELETE
                  </ArgosLoadingButton>
                }
              </ArgosFlexbox>
            </ArgosFlexbox>
          }
        </>
      </ArgosDialog>
    </div>
  )
}

export default Table;